import { useUser } from '@myosh/myosh-login';
import { useStoredValue } from '@myosh/odin-components';
import { createParser, parseAsArrayOf, parseAsInteger, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useCallback } from 'react';
import { useSchedulePage } from '@/hooks/use-schedule-page';
import { DATE_RANGE_OPTIONS, isIsoDate } from '@/utils/date-ranges';
import {
  DEFAULT_FILTER_SELECTION,
  type FilterSelection,
  normalizeSelection,
  parseStoredSelection,
} from '@/utils/filter-selection';

const isoDateParser = createParser({
  parse: (value) => (isIsoDate(value) ? value : null),
  serialize: (value: string) => value,
});

const filterParsers = {
  range: parseAsStringLiteral(DATE_RANGE_OPTIONS),
  from: isoDateParser,
  to: isoDateParser,
  forms: parseAsArrayOf(parseAsInteger),
  hierarchy: parseAsArrayOf(parseAsInteger),
};

const urlOptions = { history: 'replace', shallow: false } as const;

// The URL is the source of truth; a launch without filter params reopens the user's last applied filters.
export const useAppliedFilters = () => {
  const [params, setParams] = useQueryStates(filterParsers, urlOptions);
  const [, setPage] = useSchedulePage();
  const { state } = useUser();
  const [stored, setStored] = useStoredValue({
    key: `scheduler:filters:${state.user?.preferred_username ?? ''}`,
    fromStored: (value) => value ?? '',
  });

  const hasUrlSelection = params.range !== null || params.forms !== null || params.hierarchy !== null;
  const applied = hasUrlSelection
    ? normalizeSelection(params)
    : (parseStoredSelection(stored) ?? DEFAULT_FILTER_SELECTION);

  const apply = useCallback(
    (next: FilterSelection) => {
      const isCustom = next.range === 'custom';
      void setParams({
        range: next.range,
        from: isCustom ? next.from : null,
        to: isCustom ? next.to : null,
        forms: next.forms.length > 0 ? next.forms : null,
        hierarchy: next.hierarchy.length > 0 ? next.hierarchy : null,
      });
      void setPage(null);
      setStored(JSON.stringify(next));
    },
    [setParams, setPage, setStored]
  );

  return { applied, apply };
};
